import { useState, useEffect } from "react";
import {
  Building2, Plus, Trash2, Users, ChevronRight,
  X, CheckCircle, AlertCircle, RefreshCw, Shield, Edit2, ExternalLink, Send,
} from "lucide-react";
import BedrijfAdminPanel from "../BedrijfAdminPanel";

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl animate-fadeIn
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

// ── Bedrijf aanmaken modal ────────────────────────────────────────────────────
function CreateBedrijfModal({ token, onClose, onCreated }) {
  const [naam, setNaam] = useState("");
  const [slug, setSlug] = useState("");
  const [adminNaam, setAdminNaam] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // 1: bedrijf, 2: admin account
  const [createdCompany, setCreatedCompany] = useState(null);

  const slugify = (text) => text.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleNaamChange = (v) => {
    setNaam(v);
    setSlug(slugify(v));
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!naam.trim() || !slug.trim()) { setError("Vul bedrijfsnaam en slug in"); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ naam, slug }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Aanmaken mislukt");
      setCreatedCompany(d);
      setStep(2);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!adminNaam || !adminEmail || !adminPassword) { setError("Vul alle velden in"); return; }
    if (adminPassword.length < 8) { setError("Wachtwoord minimaal 8 tekens"); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/companies/${createdCompany.id}/invite-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ naam: adminNaam, email: adminEmail, password: adminPassword }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Admin aanmaken mislukt");
      onCreated();
      onClose();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-md w-full animate-fadeIn">
        {/* Steps indicator */}
        <div className="flex items-center gap-3 mb-6">
          <div className={`flex items-center gap-2 text-sm font-semibold ${step >= 1 ? "text-[#C9A84C]" : "text-zinc-600"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${step >= 1 ? "bg-[#C9A84C] text-black" : "bg-zinc-800 text-zinc-500"}`}>1</div>
            Bedrijf
          </div>
          <ChevronRight size={14} className="text-zinc-700" />
          <div className={`flex items-center gap-2 text-sm font-semibold ${step >= 2 ? "text-[#C9A84C]" : "text-zinc-600"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${step >= 2 ? "bg-[#C9A84C] text-black" : "bg-zinc-800 text-zinc-500"}`}>2</div>
            Admin Account
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={handleCreateCompany}>
            <h2 className="text-white font-bold text-lg mb-1">Nieuw bedrijf aanmaken</h2>
            <p className="text-zinc-500 text-sm mb-5">Sportschool, gym of coach-account instellen.</p>

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Bedrijfsnaam</label>
            <input value={naam} onChange={e => handleNaamChange(e.target.value)} placeholder="CrossFit Amsterdam"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="bedrijf-naam-input" />

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">URL Slug (uniek)</label>
            <div className="flex items-center bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 mb-4">
              <span className="text-zinc-600 text-sm mr-1">fitsmith.nl/</span>
              <input value={slug} onChange={e => setSlug(slugify(e.target.value))} placeholder="crossfit-amsterdam"
                className="flex-1 bg-transparent text-white text-sm focus:outline-none"
                data-testid="bedrijf-slug-input" />
            </div>

            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 py-3 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-white/20 transition-colors">
                Annuleren
              </button>
              <button type="submit" disabled={loading} data-testid="create-bedrijf-btn"
                className="flex-1 py-3 text-black font-bold text-sm rounded-xl disabled:opacity-40 transition-opacity"
                style={{ background: "#C9A84C" }}>
                {loading ? "Bezig..." : "Volgende →"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleCreateAdmin}>
            <h2 className="text-white font-bold text-lg mb-1">Admin account aanmaken</h2>
            <p className="text-zinc-500 text-sm mb-1">Voor: <strong className="text-[#C9A84C]">{createdCompany?.naam}</strong></p>
            <p className="text-zinc-500 text-sm mb-5">Maak het inlogaccount voor de eigenaar van dit bedrijf.</p>

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Naam</label>
            <input value={adminNaam} onChange={e => setAdminNaam(e.target.value)} placeholder="Jan de Vries"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="admin-naam-input" />

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">E-mailadres</label>
            <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="jan@crossfit-amsterdam.nl"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="admin-email-input" />

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Wachtwoord</label>
            <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} placeholder="Minimaal 8 tekens"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="admin-password-input" />

            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)}
                className="flex-1 py-3 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-white/20 transition-colors">
                ← Terug
              </button>
              <button type="submit" disabled={loading} data-testid="create-admin-btn"
                className="flex-1 py-3 text-black font-bold text-sm rounded-xl disabled:opacity-40 transition-opacity"
                style={{ background: "#C9A84C" }}>
                {loading ? "Aanmaken..." : "Bedrijf aanmaken"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Bedrijf detail modal ──────────────────────────────────────────────────────
function BedrijfDetailModal({ bedrijf, token, onClose, onUpdated }) {
  const [adminNaam, setAdminNaam] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showKleuren, setShowKleuren] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(bedrijf.branding?.primary_color || "#C9A84C");
  const [secondaryColor, setSecondaryColor] = useState(bedrijf.branding?.secondary_color || "#1a1a1a");
  const [accentColor, setAccentColor] = useState(bedrijf.branding?.accent_color || "#ffffff");
  const [opslaanKleuren, setOpslaanKleuren] = useState(false);
  const [succesKleuren, setSuccesKleuren] = useState("");
  const [showSporters, setShowSporters] = useState(false);
  const [sporters, setSporters] = useState([]);
  const [sportersLoading, setSportersLoading] = useState(false);
  const [selectedSporter, setSelectedSporter] = useState(null);
  const [showIntakeEdit, setShowIntakeEdit] = useState(false);
  const [intakeEditSporter, setIntakeEditSporter] = useState(null);
  const [intakeEdit, setIntakeEdit] = useState({});
  const [intakeSaving, setIntakeSaving] = useState(false);
  const [resendLoading, setResendLoading] = useState(null); // sporter ID die bezig is
  const [resendMsg, setResendMsg] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadSporters = async () => {
    setSportersLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/companies/${bedrijf.id}/sporters`, auth);
      if (r.ok) setSporters(await r.json());
    } catch {}
    finally { setSportersLoading(false); }
  };

  const handleShowSporters = () => {
    setShowSporters(v => !v);
    if (!showSporters && sporters.length === 0) loadSporters();
  };

  const handleResendUitnodiging = async (sporter) => {
    setResendLoading(sporter.id);
    setResendMsg("");
    try {
      const r = await fetch(
        `${API}/api/admin/companies/${bedrijf.id}/sporters/${sporter.id}/resend-uitnodiging`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Versturen mislukt");
      setResendMsg(`Verstuurd naar ${d.email}!`);
    } catch (err) {
      setResendMsg(`Fout: ${err.message}`);
    } finally {
      setResendLoading(null);
    }
  };

  const handleSaveIntake = async () => {
    if (!intakeEditSporter) return;
    setIntakeSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/companies/${bedrijf.id}/sporters/${intakeEditSporter.id}/intake`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(intakeEdit),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      // Herlaad sporterslijst zodat de badge bijwerkt
      await loadSporters();
      setShowIntakeEdit(false);
    } catch {}
    finally { setIntakeSaving(false); }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!adminNaam || !adminEmail || !adminPassword) { setError("Vul alle velden in"); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/companies/${bedrijf.id}/invite-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ naam: adminNaam, email: adminEmail, password: adminPassword }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Mislukt");
      setAdminNaam(""); setAdminEmail(""); setAdminPassword("");
      setShowAddAdmin(false);
      onUpdated();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const slaKleurenOp = async () => {
    setOpslaanKleuren(true);
    try {
      const r = await fetch(`${API}/api/companies/${bedrijf.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          branding: {
            ...bedrijf.branding,
            primary_color: primaryColor,
            secondary_color: secondaryColor,
            accent_color: accentColor,
          },
        }),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      setSuccesKleuren("Kleuren opgeslagen!");
      onUpdated();
      setTimeout(() => setSuccesKleuren(""), 3000);
    } catch (err) { setError(err.message); }
    finally { setOpslaanKleuren(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-md w-full animate-fadeIn max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-white font-bold text-lg">{bedrijf.naam}</h2>
            <p className="text-zinc-500 text-sm">slug: {bedrijf.slug}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: "Admins", val: bedrijf.admin_count ?? 0 },
            { label: "Sporters", val: bedrijf.sporter_count ?? 0 },
            { label: "Schema's", val: bedrijf.schema_count ?? 0 },
          ].map(({ label, val }) => (
            <div key={label} className="bg-[#1a1a1a] border border-white/5 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-[#C9A84C]">{val}</div>
              <div className="text-zinc-500 text-xs">{label}</div>
            </div>
          ))}
        </div>

        <p className="text-zinc-500 text-xs mb-5">
          Aangemaakt: {bedrijf.created_at ? new Date(bedrijf.created_at).toLocaleDateString("nl-NL") : "–"}
        </p>

        {/* Branding kleuren bewerken */}
        <div className="mb-4">
          <button onClick={() => setShowKleuren(!showKleuren)}
            data-testid="branding-kleuren-btn"
            className="w-full py-2.5 bg-[#1a1a1a] border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-[#C9A84C]/30 hover:text-[#C9A84C] transition-colors flex items-center justify-center gap-2 mb-3">
            <div className="flex gap-1">
              {[primaryColor, secondaryColor, accentColor].map((c, i) => (
                <div key={i} className="w-3 h-3 rounded-full border border-white/20" style={{ background: c }} />
              ))}
            </div>
            Branding kleuren aanpassen
          </button>
          {showKleuren && (
            <div className="bg-[#1a1a1a] border border-white/8 rounded-xl p-4 mb-3">
              {succesKleuren && (
                <div className="mb-3 text-green-400 text-xs font-semibold">{succesKleuren}</div>
              )}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-xs font-semibold">Primaire kleur</p>
                    <p className="text-zinc-600 text-xs">Knoppen en accenten</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-xs font-mono">{primaryColor}</span>
                    <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                      data-testid="primary-color-picker"
                      className="w-8 h-8 rounded-lg border-0 cursor-pointer bg-transparent" />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-xs font-semibold">Secundaire kleur</p>
                    <p className="text-zinc-600 text-xs">Achtergronden en kaarten</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-xs font-mono">{secondaryColor}</span>
                    <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                      data-testid="secondary-color-picker"
                      className="w-8 h-8 rounded-lg border-0 cursor-pointer bg-transparent" />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-xs font-semibold">Accentkleur</p>
                    <p className="text-zinc-600 text-xs">Tekst en highlights</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-xs font-mono">{accentColor}</span>
                    <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                      data-testid="accent-color-picker"
                      className="w-8 h-8 rounded-lg border-0 cursor-pointer bg-transparent" />
                  </div>
                </div>
              </div>
              <button onClick={slaKleurenOp} disabled={opslaanKleuren}
                data-testid="kleuren-opslaan-btn"
                className="w-full mt-3 py-2.5 text-black font-bold text-sm rounded-xl disabled:opacity-40 transition-all"
                style={{ background: primaryColor }}>
                {opslaanKleuren ? "Opslaan..." : "Kleuren opslaan"}
              </button>
            </div>
          )}
        </div>

        {/* Extra admin toevoegen */}
        <button onClick={() => setShowAddAdmin(!showAddAdmin)}
          className="w-full py-2.5 bg-[#1a1a1a] border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-[#C9A84C]/30 hover:text-[#C9A84C] transition-colors flex items-center justify-center gap-2 mb-3">
          <Plus size={14} /> Extra admin toevoegen
        </button>

        {showAddAdmin && (
          <form onSubmit={handleAddAdmin} className="space-y-3">
            <input value={adminNaam} onChange={e => setAdminNaam(e.target.value)} placeholder="Naam"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50" />
            <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="E-mailadres"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50" />
            <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} placeholder="Wachtwoord"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50" />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button type="submit" disabled={loading} className="w-full py-2.5 text-black font-bold text-sm rounded-xl disabled:opacity-40" style={{ background: "#C9A84C" }}>
              {loading ? "Toevoegen..." : "Admin toevoegen"}
            </button>
          </form>
        )}

        <button onClick={onClose} className="w-full mt-3 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-white/20 transition-colors">
          Sluiten
        </button>

        {/* Sporters & Intake */}
        <button
          onClick={handleShowSporters}
          data-testid="sporters-bekijken-btn"
          className="w-full mt-3 py-2.5 bg-[#1a1a1a] border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-[#C9A84C]/30 hover:text-[#C9A84C] transition-colors flex items-center justify-center gap-2"
        >
          <Users size={14} /> {showSporters ? "Sporters verbergen" : `Sporters bekijken (${bedrijf.sporter_count ?? 0})`}
        </button>

        {showSporters && (
          <div className="mt-3 space-y-2">
            {sportersLoading ? (
              <div className="h-16 bg-[#1a1a1a] rounded-xl animate-pulse" />
            ) : sporters.length === 0 ? (
              <p className="text-zinc-600 text-xs text-center py-4">Geen sporters gevonden</p>
            ) : (
              sporters.map(s => (
                <div key={s.id} className="bg-[#1a1a1a] border border-white/5 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setSelectedSporter(selectedSporter?.id === s.id ? null : s)}
                    data-testid={`sporter-rij-${s.id}`}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/3 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex items-center justify-center text-[#C9A84C] text-xs font-bold flex-shrink-0">
                        {s.naam.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{s.naam}</p>
                        <p className="text-zinc-600 text-xs truncate">{s.email}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                      s.heeft_intake
                        ? "bg-green-500/12 text-green-400 border border-green-500/20"
                        : "bg-zinc-800 text-zinc-600 border border-white/5"
                    }`}>
                      {s.heeft_intake ? "Intake ✓" : "Geen intake"}
                    </span>
                  </button>

                  {/* Intake + heruitnodiging */}
                  {selectedSporter?.id === s.id && (
                    <div className="border-t border-white/5 px-4 py-3 bg-black/20 space-y-3">

                      {/* Uitnodiging opnieuw sturen */}
                      {s.email && (
                        <div className="flex items-center justify-between gap-3 p-2.5 bg-[#1a1a1a] border border-white/5 rounded-xl">
                          <div className="min-w-0">
                            <p className="text-zinc-300 text-xs font-semibold">Uitnodiging opnieuw sturen</p>
                            <p className="text-zinc-600 text-[10px] truncate">Stuurt nieuwe inloggegevens naar {s.email}</p>
                          </div>
                          <button
                            onClick={() => handleResendUitnodiging(s)}
                            disabled={resendLoading === s.id}
                            data-testid={`admin-resend-uitnodiging-${s.id}`}
                            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-40 transition-all"
                            style={{ background: "#C9A84C", color: "#000" }}
                          >
                            {resendLoading === s.id
                              ? <RefreshCw size={10} className="animate-spin" />
                              : <Send size={10} />
                            }
                            {resendLoading === s.id ? "Sturen..." : "Sturen"}
                          </button>
                        </div>
                      )}
                      {resendMsg && selectedSporter?.id === s.id && (
                        <p className="text-xs text-center font-semibold" style={{ color: resendMsg.startsWith("Fout") ? "#f87171" : "#4ade80" }}>
                          {resendMsg}
                        </p>
                      )}

                      {/* Intake data */}
                      {s.heeft_intake ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-zinc-500 text-[10px] font-semibold uppercase tracking-widest">Intake gegevens</p>
                            <button
                              onClick={() => { setIntakeEditSporter(s); setIntakeEdit(s.antwoorden || {}); setShowIntakeEdit(true); }}
                              data-testid={`admin-edit-intake-btn-${s.id}`}
                              className="text-[10px] text-zinc-500 hover:text-[#C9A84C] transition-colors flex items-center gap-1"
                            >
                              <Edit2 size={10} /> Aanpassen
                            </button>
                          </div>
                          {[
                            { label: "Trainingsdoel", key: "trainingsdoel", icon: "🎯" },
                            { label: "Klachten / blessures", key: "klachten", icon: "🩺" },
                            { label: "Ervaring", key: "trainingservaring", icon: "💪" },
                            { label: "Trainingsdagen/week", key: "trainingsdagen", icon: "📅" },
                            { label: "Gewicht", key: "gewicht", suffix: "kg", icon: "⚖️" },
                            { label: "Lengte", key: "lengte", suffix: "cm", icon: "📏" },
                            { label: "Medicatie", key: "medicatie", icon: "💊" },
                            { label: "Overige info", key: "overige_info", icon: "📝" },
                          ].map(({ label, key, icon, suffix }) => {
                            const val = s.antwoorden?.[key];
                            if (!val) return null;
                            return (
                              <div key={key} className="flex items-start gap-2 text-xs">
                                <span className="text-sm leading-tight mt-0.5">{icon}</span>
                                <div>
                                  <p className="text-zinc-600 text-[10px]">{label}</p>
                                  <p className="text-white font-medium">{val}{suffix ? ` ${suffix}` : ""}</p>
                                </div>
                              </div>
                            );
                          })}
                          {s.ingevuld_op && (
                            <p className="text-zinc-700 text-[10px] pt-1 border-t border-white/5">
                              Ingevuld: {new Date(s.ingevuld_op).toLocaleDateString("nl-NL")}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <p className="text-zinc-600 text-xs mb-2">
                            Deze sporter heeft nog geen intake ingevuld
                          </p>
                          <button
                            onClick={() => { setIntakeEditSporter(s); setIntakeEdit({}); setShowIntakeEdit(true); }}
                            data-testid={`admin-create-intake-btn-${s.id}`}
                            className="text-xs text-[#C9A84C] hover:underline"
                          >
                            Zelf invullen →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        <button onClick={onClose} className="w-full mt-3 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl hover:border-white/20 transition-colors">
          Sluiten
        </button>
      </div>

      {/* Intake bewerken modal (super-admin) */}
      {showIntakeEdit && intakeEditSporter && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-[#111] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/5 flex-shrink-0">
              <div>
                <h3 className="text-white font-bold">Intake aanpassen</h3>
                <p className="text-zinc-500 text-xs mt-0.5">{intakeEditSporter.naam}</p>
              </div>
              <button onClick={() => setShowIntakeEdit(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-3">
              {[
                { label: "Trainingsdoel", key: "trainingsdoel", placeholder: "bijv. Afvallen, Spiermassa, Conditie...", type: "textarea" },
                { label: "Klachten / blessures", key: "klachten", placeholder: "bijv. Knieklachten, rugpijn of geen klachten", type: "textarea" },
                { label: "Trainingservaring", key: "trainingservaring", placeholder: "bijv. Beginner, 2 jaar ervaring..." },
                { label: "Trainingsdagen per week", key: "trainingsdagen", placeholder: "bijv. 3", type: "number" },
                { label: "Gewicht (kg)", key: "gewicht", placeholder: "bijv. 75", type: "number" },
                { label: "Lengte (cm)", key: "lengte", placeholder: "bijv. 178", type: "number" },
                { label: "Medicatie", key: "medicatie", placeholder: "bijv. Geen of omschrijving" },
                { label: "Overige informatie", key: "overige_info", placeholder: "Extra aandachtspunten...", type: "textarea" },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key}>
                  <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1.5">{label}</label>
                  {type === "textarea" ? (
                    <textarea
                      value={intakeEdit[key] || ""}
                      onChange={e => setIntakeEdit(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={placeholder}
                      rows={2}
                      className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-[#C9A84C]/40"
                      data-testid={`admin-intake-field-${key}`}
                    />
                  ) : (
                    <input
                      type={type || "text"}
                      value={intakeEdit[key] || ""}
                      onChange={e => setIntakeEdit(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={placeholder}
                      className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40"
                      data-testid={`admin-intake-field-${key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-white/5 flex-shrink-0 flex gap-3">
              <button onClick={() => setShowIntakeEdit(false)} className="flex-1 py-3 rounded-xl text-sm font-bold border border-white/8 text-zinc-400">
                Annuleren
              </button>
              <button
                onClick={handleSaveIntake}
                disabled={intakeSaving}
                data-testid="admin-save-intake-btn"
                className="flex-1 py-3 rounded-xl text-black font-bold text-sm disabled:opacity-60"
                style={{ background: "#C9A84C" }}
              >
                {intakeSaving ? "Opslaan..." : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bedrijven Tab (Super Admin) ───────────────────────────────────────────────
export default function BedrijvenTab({ token }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBedrijf, setSelectedBedrijf] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [impersonateLoading, setImpersonateLoading] = useState(null);
  const [planModal, setPlanModal] = useState(null); // company object
  const [planSaving, setPlanSaving] = useState(false);
  const [planForm, setPlanForm] = useState({ plan: "basic", betaal_methode: "mollie", demo_dagen: 14, notitie: "" });

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const openPlanModal = (c) => {
    setPlanForm({
      plan: c.plan || "basic",
      betaal_methode: c.betaal_methode || "mollie",
      demo_dagen: c.demo_dagen || 14,
      notitie: c.admin_notitie || "",
    });
    setPlanModal(c);
  };

  const savePlan = async () => {
    if (!planModal) return;
    setPlanSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/companies/${planModal.id}/plan`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(planForm),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      showToast("Abonnement bijgewerkt");
      setPlanModal(null);
      load();
    } catch (err) { showToast(err.message, "error"); }
    finally { setPlanSaving(false); }
  };

  const openBedrijfsOmgeving = async (company) => {
    setImpersonateLoading(company.id);
    try {
      const r = await fetch(`${API}/api/admin/impersonate-company/${company.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { showToast("Geen bedrijfsadmin gevonden voor dit bedrijf", "error"); return; }
      const d = await r.json();
      setImpersonating({ token: d.token, company_naam: d.company_naam });
    } catch { showToast("Openen mislukt", "error"); }
    finally { setImpersonateLoading(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setCompanies(d.companies || []);
    } catch { showToast("Laden mislukt", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/api/companies/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      setDeleteConfirm(null);
      showToast("Bedrijf verwijderd");
      load();
    } catch (err) { showToast(err.message, "error"); }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Multi-tenant B2B</p>
          <h2 className="text-xl font-bold text-white">Bedrijven</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="p-2 text-zinc-500 hover:text-white transition-colors" title="Verversen">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="create-bedrijf-open-btn"
            className="flex items-center gap-2 px-5 py-2.5 text-black font-bold text-sm rounded-xl transition-opacity hover:opacity-90"
            style={{ background: "#C9A84C" }}>
            <Plus size={15} /> Bedrijf aanmaken
          </button>
        </div>
      </div>

      {/* Overview stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {[
          { label: "Totaal bedrijven", val: companies.length, icon: <Building2 size={18} /> },
          { label: "Actief", val: companies.filter(c => c.is_active).length, icon: <CheckCircle size={18} /> },
          { label: "Totaal sporters", val: companies.reduce((a, c) => a + (c.sporter_count || 0), 0), icon: <Users size={18} /> },
        ].map(({ label, val, icon }) => (
          <div key={label} className="course-stat-card">
            <div className="text-[#C9A84C] mb-2">{icon}</div>
            <div className="text-2xl font-bold text-white">{val}</div>
            <div className="text-zinc-500 text-xs mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Bedrijven lijst */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-600">
          <RefreshCw size={20} className="animate-spin mr-3" /> Laden...
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-20">
          <Building2 size={48} className="text-zinc-800 mx-auto mb-4" />
          <p className="text-zinc-500 font-semibold mb-1">Nog geen bedrijven</p>
          <p className="text-zinc-600 text-sm mb-6">Maak het eerste bedrijfsaccount aan voor een sportschool of coach.</p>
          <button onClick={() => setShowCreate(true)} className="px-6 py-3 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
            Eerste bedrijf aanmaken
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {companies.map(c => (
            <div key={c.id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-5 hover:border-[#C9A84C]/20 transition-colors group">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {c.branding?.logo_url ? (
                    <img src={c.branding.logo_url} alt={c.naam} className="w-10 h-10 rounded-xl object-cover border border-white/10" />
                  ) : (
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black text-black"
                      style={{ background: c.branding?.primary_color || "#C9A84C" }}>
                      {c.naam.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-white font-bold text-sm">{c.naam}</h3>
                    <p className="text-zinc-600 text-xs">/{c.slug}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${c.is_active ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                    {c.is_active ? "Actief" : "Inactief"}
                  </span>
                  {c.plan && (
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                      c.plan === "business" ? "bg-amber-500/15 text-amber-400" :
                      c.plan === "pro"      ? "bg-[#C9A84C]/15 text-[#C9A84C]" :
                                             "bg-zinc-500/10 text-zinc-400"
                    }`}>{c.plan}</span>
                  )}
                  {c.betaal_methode === "demo" && c.demo_einde && (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">
                      Demo t/m {new Date(c.demo_einde).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
                    </span>
                  )}
                  {c.betaal_methode && c.betaal_methode !== "demo" && (
                    <span className="text-[10px] text-zinc-600">{c.betaal_methode}</span>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <Shield size={12} className="text-zinc-600" />
                  <span>{c.admin_count || 0} admin</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <Users size={12} className="text-zinc-600" />
                  <span>{c.sporter_count || 0} sporters</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <CheckCircle size={12} className="text-zinc-600" />
                  <span>{c.schema_count || 0} schema's</span>
                </div>
              </div>

              {/* Owner */}
              {c.owner_email && (
                <p className="text-zinc-600 text-xs mb-4 truncate">{c.owner_email}</p>
              )}

              {/* Branding preview */}
              {c.branding && (
                <div className="flex gap-1.5 mb-4">
                  {[c.branding.primary_color, c.branding.secondary_color, c.branding.accent_color]
                    .filter(Boolean).map((col, i) => (
                    <div key={i} className="w-4 h-4 rounded-full border border-white/10" style={{ background: col }} />
                  ))}
                </div>
              )}

              {/* Acties */}
              <div className="flex gap-2 pt-3 border-t border-white/5">
                <button
                  onClick={() => openBedrijfsOmgeving(c)}
                  data-testid={`open-omgeving-${c.id}`}
                  disabled={impersonateLoading === c.id}
                  className="flex-1 py-2 text-xs font-semibold text-[#C9A84C] hover:text-black bg-[#C9A84C]/10 hover:bg-[#C9A84C] rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {impersonateLoading === c.id
                    ? <RefreshCw size={12} className="animate-spin" />
                    : <ExternalLink size={12} />}
                  {impersonateLoading === c.id ? "Laden..." : "Open omgeving"}
                </button>
                <button
                  onClick={() => openPlanModal(c)}
                  data-testid={`plan-btn-${c.id}`}
                  className="py-2 px-3 text-xs font-semibold text-zinc-300 hover:text-white bg-white/4 hover:bg-white/8 rounded-lg transition-colors flex items-center gap-1.5">
                  <Shield size={12} /> Abonnement
                </button>
                <button
                  onClick={() => setSelectedBedrijf(c)}
                  data-testid={`view-bedrijf-${c.id}`}
                  className="py-2 px-3 text-xs font-semibold text-zinc-400 hover:text-white bg-white/4 hover:bg-white/8 rounded-lg transition-colors flex items-center justify-center gap-1.5">
                  <Edit2 size={12} />
                </button>
                <button
                  onClick={() => setDeleteConfirm(c)}
                  data-testid={`delete-bedrijf-${c.id}`}
                  className="py-2 px-3 text-xs font-semibold text-zinc-600 hover:text-red-400 bg-white/4 hover:bg-red-500/10 rounded-lg transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {/* Plan/Abonnement Modal */}
      {planModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
              <div>
                <p className="text-white font-bold text-base">Abonnement instellen</p>
                <p className="text-zinc-500 text-xs mt-0.5">{planModal.naam}</p>
              </div>
              <button onClick={() => setPlanModal(null)} className="text-zinc-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">

              {/* Actieve demo banner — toon huidige demo + verwijder-knop */}
              {planModal.betaal_methode === "demo" && planModal.demo_einde && (
                <div className="flex items-center justify-between bg-blue-500/8 border border-blue-500/20 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-blue-400 text-xs font-bold">Actieve demo periode</p>
                    <p className="text-zinc-400 text-xs mt-0.5">
                      Loopt t/m <span className="text-white font-semibold">
                        {new Date(planModal.demo_einde).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => setPlanForm(f => ({ ...f, betaal_methode: "mollie" }))}
                    data-testid="demo-verwijderen-btn"
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 transition-colors whitespace-nowrap ml-3">
                    Demo verwijderen
                  </button>
                </div>
              )}

              {/* Plan selectie */}
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Pakket</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { val: "basic",    label: "BASIC",    prijs: "€49/4w",  kleur: "#6b7280" },
                    { val: "pro",      label: "PRO",      prijs: "€99/4w",  kleur: "#C9A84C" },
                    { val: "business", label: "BUSINESS", prijs: "€159/4w", kleur: "#f59e0b" },
                  ].map(p => (
                    <button key={p.val} onClick={() => setPlanForm(f => ({ ...f, plan: p.val }))}
                      data-testid={`plan-option-${p.val}`}
                      className={`py-3 px-2 rounded-xl border text-center transition-all ${planForm.plan === p.val ? "border-[#C9A84C]" : "border-white/8 hover:border-white/20"}`}
                      style={planForm.plan === p.val ? { background: `${p.kleur}15` } : { background: "#1a1a1a" }}>
                      <p className="text-white font-bold text-xs">{p.label}</p>
                      <p className="text-zinc-500 text-[10px]">{p.prijs}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Betaalmethode */}
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Betaalmethode</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: "mollie",   label: "Online (Mollie)", icon: "💳" },
                    { val: "contant",  label: "Contant",         icon: "💵" },
                    { val: "factuur",  label: "Op factuur",      icon: "📄" },
                    { val: "demo",     label: "Demo periode",    icon: "⏳" },
                  ].map(m => (
                    <button key={m.val} onClick={() => setPlanForm(f => ({ ...f, betaal_methode: m.val }))}
                      data-testid={`betaal-option-${m.val}`}
                      className={`py-2.5 px-3 rounded-xl border text-left transition-all ${planForm.betaal_methode === m.val ? "border-[#C9A84C] bg-[#C9A84C]/8" : "border-white/8 hover:border-white/20 bg-[#1a1a1a]"}`}>
                      <span className="text-sm mr-1.5">{m.icon}</span>
                      <span className={`text-xs font-semibold ${planForm.betaal_methode === m.val ? "text-[#C9A84C]" : "text-zinc-300"}`}>{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Demo dagen (alleen zichtbaar bij demo) */}
              {planForm.betaal_methode === "demo" && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-2">Demo duur</p>
                  <div className="flex items-center gap-2">
                    {[7, 14, 30].map(d => (
                      <button key={d} onClick={() => setPlanForm(f => ({ ...f, demo_dagen: d }))}
                        data-testid={`demo-dagen-${d}`}
                        className={`flex-1 py-2 rounded-xl border text-xs font-bold transition-all ${planForm.demo_dagen === d ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]" : "border-white/8 text-zinc-400 hover:border-white/20 bg-[#1a1a1a]"}`}>
                        {d} dagen
                      </button>
                    ))}
                    <input
                      type="number" min="1" max="365"
                      value={planForm.demo_dagen}
                      onChange={e => setPlanForm(f => ({ ...f, demo_dagen: parseInt(e.target.value) || 14 }))}
                      className="w-20 bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-xs text-center focus:outline-none focus:border-[#C9A84C]/50"
                      placeholder="Eigen"
                    />
                  </div>
                </div>
              )}

              {/* Notitie */}
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-2">Interne notitie <span className="text-zinc-600 normal-case">(optioneel)</span></p>
                <textarea
                  value={planForm.notitie}
                  onChange={e => setPlanForm(f => ({ ...f, notitie: e.target.value }))}
                  placeholder="Bijv. 'Betaalt eerste maand cash, daarna Mollie'"
                  rows={2}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-700"
                  data-testid="plan-notitie-input"
                />
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => setPlanModal(null)}
                className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:text-white hover:border-white/20 transition-colors">
                Annuleren
              </button>
              <button onClick={savePlan} disabled={planSaving}
                data-testid="plan-save-btn"
                className="flex-1 py-3 rounded-xl text-black text-sm font-black transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: "#C9A84C" }}>
                {planSaving ? "Opslaan..." : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bedrijfsomgeving impersonation modal */}
      {impersonating && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-[#080808]">
          <BedrijfAdminPanel
            impersonateToken={impersonating.token}
            onCloseImpersonate={() => setImpersonating(null)}
          />
        </div>
      )}

      {showCreate && (
        <CreateBedrijfModal token={token} onClose={() => setShowCreate(false)} onCreated={() => { load(); showToast("Bedrijf aangemaakt!"); }} />
      )}

      {selectedBedrijf && (
        <BedrijfDetailModal
          bedrijf={selectedBedrijf}
          token={token}
          onClose={() => setSelectedBedrijf(null)}
          onUpdated={() => { load(); showToast("Bijgewerkt!"); }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full animate-fadeIn">
            <h3 className="text-white font-bold text-lg mb-2">Bedrijf verwijderen</h3>
            <p className="text-zinc-400 text-sm mb-2">
              Weet je zeker dat je <strong className="text-white">{deleteConfirm.naam}</strong> wilt verwijderen?
            </p>
            <p className="text-red-400/80 text-xs mb-6">
              Dit verwijdert ook alle gekoppelde admins, sporters en schema's. Dit kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">Annuleren</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-bedrijf-btn"
                className="flex-1 py-2.5 bg-red-900/40 border border-red-500/30 text-red-400 font-semibold text-sm rounded-xl hover:bg-red-900/60 transition-colors">
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
